/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {Typography} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {SystemFavouriteFilledIcon} from '@ion/dls/src/icons/current/SystemFavouriteFilledIcon';
import {StarsFillIcon} from '@ion/dls/src/icons/StarsFillIcon';
import {StarsIcon} from '@ion/dls/src/icons/StarsIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {IconSize} from '@ion/dls/src/types/types';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {removeTempQueries} from './ModularSearchWidget';
import {useStyles} from './SearchSuggestions';
import {SavedQueryItem} from './utils';

interface SavedQueriesListProps {
  pageName: string;
  onSavedQuerySelect: (query: any) => void;
  createChips?: Function;
  queryName?: string;
}

const SaveQueriesList = ({
  pageName,
  queryName,
  onSavedQuerySelect
}: SavedQueriesListProps) => {
  const classes= useStyles({
    isFixedWidth : true
  });

  const {isCurrent : isCurrentTheme} = useThemeContext();

  const savedQueries = removeTempQueries(pageName);

  const selectQuery = useCallback(function({name, query}) {
    return () => {
      onSavedQuerySelect({name, query});
    };
  }, []);

  if (savedQueries?.query?.length) {
    return (
      <div className={isCurrentTheme ? classes.favouritesActions : ''}>
        {!isNil(queryName) && (
          <MenuItem key='saved-query-selected'
            classes={{root: `${classes.suggestionItem}, ${classes.selectedListItem}`}}>
            <Render>
              <Render.If condition={isCurrentTheme}>
                <SystemFavouriteFilledIcon size={IconSize.medium} color={dlsColor('lightRed')} stroke={dlsColor('red')} />
              </Render.If>
              <Render.Else>
                <StarsFillIcon size={IconSize.medium} color={dlsColor('grey3')} />
              </Render.Else>
            </Render>
            <LabelTypography className={classes.label}>{queryName}</LabelTypography>
          </MenuItem>
        )}
        {savedQueries.query.map(({name, query}: SavedQueryItem, index: number) => {
          if (name && name !== queryName && query && safeJSONParse(query)?.length) {
            return (
              <MenuItem key={`saved-query${index}`}
                classes={{root: classes.suggestionItem}}
                onClick={selectQuery({name, query})}>
                <Render>
                  <Render.If condition={isCurrentTheme}>
                    <SystemFavouriteFilledIcon size={IconSize.medium} color={dlsColor('lightRed')} stroke={dlsColor('red')} />
                  </Render.If>
                  <Render.Else>
                    <StarsIcon size={IconSize.small} />
                  </Render.Else>
                </Render>
                <LabelTypography className={classes.label}>{name}</LabelTypography>
              </MenuItem>
            );
          }
          return null;
        })}
      </div>
    );
  }

  return (
    <MenuItem disabled>
      <Typography style={{textAlign: 'center', fontSize: 12}}>
        {translate('common:modularsearch.nosavedquery')}
      </Typography>
    </MenuItem>
  );
};

export default SaveQueriesList;
